import { FeedbackSubmission, Feedback, FeedbackStats } from '../types/feedback'
import { getAPIHeaders } from '../utils/apiHeaders'

/**
 * Feedback Service for submitting and managing user feedback
 */
class FeedbackService {
  private baseUrl: string

  constructor() {
    // Use empty string for Vite proxy in dev, full URL in production
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  }

  /**
   * Submit user feedback
   */
  async submitFeedback(data: FeedbackSubmission): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/feedback`, {
      method: 'POST',
      headers: getAPIHeaders(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to submit feedback' }))
      throw new Error(error.error || 'Failed to submit feedback')
    }
  }

  /**
   * Get all feedback (admin)
   */
  async getAllFeedback(filters?: { status?: string; rating?: string }): Promise<Feedback[]> {
    const params = new URLSearchParams(filters as any)
    const response = await fetch(`${this.baseUrl}/api/feedback?${params}`)

    if (!response.ok) {
      throw new Error('Failed to fetch feedback')
    }

    return response.json()
  }

  /**
   * Get feedback statistics (admin)
   */
  async getStats(): Promise<FeedbackStats> {
    const response = await fetch(`${this.baseUrl}/api/feedback/stats`)

    if (!response.ok) {
      throw new Error('Failed to fetch stats')
    }

    return response.json()
  }

  /**
   * Update feedback status (admin)
   */
  async updateStatus(id: string, status: string, adminNotes?: string): Promise<Feedback> {
    const response = await fetch(`${this.baseUrl}/api/feedback/${id}`, {
      method: 'PATCH',
      headers: getAPIHeaders(),
      body: JSON.stringify({ status, adminNotes }),
    })

    if (!response.ok) {
      throw new Error('Failed to update feedback')
    }

    return response.json()
  }

  /**
   * Delete feedback (admin)
   */
  async deleteFeedback(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/feedback/${id}`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error('Failed to delete feedback')
    }
  }
}

// Export singleton instance
export const feedbackService = new FeedbackService()
export default feedbackService
